/* TodayPage.jsx — Operational command-centre.
 *
 * "Air traffic control for property transactions."
 *
 * Calm, premium, transaction-native. Linear × Stripe × Apple.
 * The single highest-frequency page in ChainFlow.
 *
 * Sections (top → bottom):
 *   1. Header                — greeting + global actions
 *   2. KPI strip             — 5 operational signals
 *   3. Operational insight   — slim intelligence bar
 *   4. Needs attention       — operational blocker cards (hero)
 *   5. My active deals       — dense transaction board
 *   6. Lower 3-col grid      — exchanges / waiting on / today's movement
 *   7. Sticky quick actions  — one-tap operational dock
 *
 * Powered by /api/today/sheet + chainStore deals + contacts.
 * No backend changes — financial / waiting-on derivations done client-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle, ArrowRight, ArrowRightLeft, Bell, Check, ChevronDown, ChevronRight,
  Calendar, FileText, Filter, Loader2, MessageSquare, Pencil,
  Plus, PoundSterling, RefreshCw, Search, Send, Sparkles, ListChecks,
  Home, Upload, Users, UserCircle2, AlertTriangle, Briefcase, Building2,
  CheckCircle2, Inbox, MoreHorizontal, Mail,
  LayoutDashboard as LayoutDashboardIcon,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { api } from "../lib/api";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import { invalidateDealActivity } from "../hooks/useDealActivity";
import { useChainStore } from "../data/chainStore";
import { useAuth } from "../data/authStore";
import { isActiveDeal, needsAttentionDeal } from "../data/dealFilters";
import {
  ChaseSolicitorSheet, SendSellerUpdateSheet,
  UpdateMilestoneSheet, AddNoteSheet,
} from "../components/today/TodayQuickActionSheets";
import { OperationalBlockersPanel } from "../components/today/OperationalBlockersPanel";
import TodayMobileV2 from "../components/today/TodayMobileV2";
import { FollowUpsDueDesktop } from "../components/today/FollowUpsDue";
import { MorningAcknowledgement } from "../components/today/MorningAcknowledgement";
import { buildRequestDocUrl, isDocRequestRelevant } from "../lib/documentRequestLaunch";
import { resolveAgreedFeeGBP } from "../lib/feeFormat";
import {
  journeyFor,
  activeIndexForDeal,
  optsForDeal,
  resolveDealJourneyProgress,
} from "../data/transactionJourney";
import { classifyDeal } from "../data/dealHealth";

/* ════════════════════════════════════════════════════════════
   Tone tokens
   ════════════════════════════════════════════════════════════ */
const SEVERITY = {
  blocking: { fg: "#B91C1C", bg: "rgba(239,68,68,0.10)",  rail: "#EF4444", icon: "#B91C1C", iconBg: "rgba(239,68,68,0.12)" },
  warning:  { fg: "#C2410C", bg: "rgba(249,115,22,0.10)", rail: "#F97316", icon: "#C2410C", iconBg: "rgba(249,115,22,0.12)" },
  info:     { fg: "#B45309", bg: "rgba(245,158,11,0.10)", rail: "#F59E0B", icon: "#B45309", iconBg: "rgba(245,158,11,0.12)" },
};

const STATUS_CHIP = {
  "Needs signature":   { bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
  "Enquiries pending": { bg: "rgba(245,158,11,0.12)",  fg: "#B45309" },
  "Mortgage delay":    { bg: "rgba(239,68,68,0.10)",   fg: "#B91C1C" },
  "Awaiting searches": { bg: "rgba(245,158,11,0.10)",  fg: "#B45309" },
  "Stable":            { bg: "rgba(16,185,129,0.10)",  fg: "#047857" },
  "Exchanged":         { bg: "rgba(16,185,129,0.14)",  fg: "#047857" },
  "At risk":           { bg: "rgba(239,68,68,0.10)",   fg: "#B91C1C" },
  "Watch":             { bg: "rgba(245,158,11,0.12)",  fg: "#B45309" },
  "Mortgage in progress": { bg: "rgba(99,102,241,0.10)", fg: "#4338CA" },
  "Contract in progress": { bg: "rgba(99,102,241,0.10)", fg: "#4338CA" },
  "Management Pack":   { bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
  "BSA":               { bg: "rgba(99,102,241,0.10)",  fg: "#4338CA" },
};

const MOVEMENT_DOT = {
  "Document received":   "#10B981",
  "Document requested":  "#F59E0B",
  "Stage advanced":      "#6366F1",
  "Milestone reached":   "#10B981",
  "Chase sent":          "#8B5CF6",
  "Reminder sent":       "#94A3B8",
  "Exchange updated":    "#6366F1",
  "Completion updated":  "#10B981",
  "Risk cleared":        "#10B981",
  "Participant invited": "#94A3B8",
  Update:                "#94A3B8",
};

/* ════════════════════════════════════════════════════════════
   Helpers
   ════════════════════════════════════════════════════════════ */
const _MS_DAY = 86_400_000;

const relTime = (v) => {
  if (!v) return "";
  const t = typeof v === "number" ? v : Date.parse(v);
  if (Number.isNaN(t)) return "";
  const ms = Date.now() - t;
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < _MS_DAY) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / _MS_DAY)}d ago`;
};

const todayUpper = () =>
  new Date()
    .toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })
    .toUpperCase();

const todayShort = () =>
  new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const greetingFor = (now = new Date()) => {
  const h = now.getHours();
  if (h < 5)  return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Working late";
};

const fmtGBP = (n, { compact = false } = {}) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (compact && Math.abs(v) >= 1000) {
    return `£${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  }
  return `£${v.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
};

const fmtDateShort = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// Whole UK calendar days (Europe/London) to the date — same rule as the Deal Room.
const daysBetween = (iso) => {
  if (!iso) return null;
  const t = Date.parse(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(t)) return null;
  const today = Date.parse(`${new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" })}T00:00:00Z`);
  return Math.round((t - today) / _MS_DAY);
};

const initials = (name) => {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";
};

const ownerLabel = (role) => {
  if (!role) return "Responsible party";
  const r = String(role).toLowerCase();
  if (r.includes("solicitor_seller") || r === "seller_solicitor") return "Seller's solicitor";
  if (r.includes("solicitor_buyer")  || r === "buyer_solicitor")  return "Buyer's solicitor";
  if (r === "buyer")  return "Buyer";
  if (r === "seller") return "Seller";
  if (r.includes("broker") || r.includes("mortgage")) return "Mortgage broker";
  if (r.includes("management")) return "Management company";
  if (r.includes("surveyor")) return "Surveyor";
  if (r.includes("agent")) return "Agent";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const ownerGroupLabel = (role) => {
  const r = String(role || "").toLowerCase();
  if (r.includes("solicitor_seller") || r === "seller_solicitor") return "Seller solicitor";
  if (r.includes("solicitor_buyer")  || r === "buyer_solicitor")  return "Buyer solicitor";
  if (r.includes("broker") || r.includes("mortgage")) return "Buyer broker";
  if (r.includes("management")) return "Management company";
  if (r.includes("surveyor")) return "Surveyor";
  if (r === "buyer") return "Buyer";
  if (r === "seller") return "Seller";
  return ownerLabel(role);
};

const ownerIcon = (role) => {
  const r = String(role || "").toLowerCase();
  if (r.includes("solicitor")) return Users;
  if (r.includes("broker") || r.includes("mortgage")) return Briefcase;
  if (r.includes("management")) return Building2;
  if (r.includes("surveyor")) return Search;
  return Users;
};

const stageLabel = (stage = "") => {
  const s = String(stage).toLowerCase();
  if (!s) return "—";
  if (s.includes("enquir"))   return "Enquiries";
  if (s.includes("memo"))     return "Memorandum";
  if (s.includes("mortgage")) return "Mortgage offer";
  if (s.includes("search"))   return "Searches";
  if (s === "exchange_ready" || s === "exchange-ready") return "Exchange ready";
  if (s.includes("exchang"))  return "Exchanged";
  if (s.includes("complet"))  return "Completion";
  if (s === "offer")          return "Offer";
  return stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, " ");
};

const nextMilestoneLabel = (stage = "") => {
  const s = String(stage).toLowerCase();
  if (s === "offer" || s.includes("memo")) return { title: "AML & memorandum", sub: "Open" };
  // iter-alignment · Pre-pilot coherence (2026-02).  Stage-
  // specific momentum copy replaces the generic "Maintain
  // momentum" fallback so each active stage carries an
  // operational instruction.
  if (s === "aml")             return { title: "Complete AML checks",  sub: "Confirm evidence" };
  if (s === "solicitors")      return { title: "Issue draft contracts", sub: "Confirm receipt" };
  if (s === "survey")          return { title: "Survey outcome",        sub: "Confirm result" };
  if (s === "mortgage_app" || s === "mortgage-app") return { title: "Mortgage valuation", sub: "Confirm booking" };
  if (s.includes("enquir"))   return { title: "Resolve enquiries", sub: "Open" };
  if (s.includes("search"))   return { title: "Searches return", sub: "Awaiting" };
  if (s.includes("mortgage")) return { title: "Mortgage offer", sub: "Chase broker" };
  if (s === "exchange_ready" || s === "exchange-ready") return { title: "Exchange contracts", sub: "Awaiting signature" };
  if (s.includes("exchang"))  return { title: "Complete", sub: "On track" };
  return { title: "Maintain momentum", sub: "—" };
};

const stageProgress = (stage = "") => {
  const order = ["offer", "memorandum", "enquiries", "searches", "mortgage_offer", "exchange_ready", "exchanged", "completion", "completed"];
  const s = String(stage).toLowerCase();
  let idx = order.findIndex((o) => s.startsWith(o));
  if (idx < 0) {
    if (s.includes("memo")) idx = 1;
    else if (s.includes("enquir")) idx = 2;
    else if (s.includes("search")) idx = 3;
    else if (s.includes("mortgage")) idx = 4;
    else if (s.includes("exchange_ready") || s === "exchange-ready") idx = 5;
    else if (s.includes("exchang")) idx = 6;
    else if (s.includes("complet")) idx = 8;
    else idx = 0;
  }
  return Math.round(((idx + 1) / order.length) * 100);
};

/* iter-alignment · P0 · Command Centre Data Reconciliation (2026-02-28)
 * — canonical Deal Rooms + Deal Room detail both compute progress
 * through `activeIndexForDeal(deal, optsForDeal(deal))` against the
 * tenure-aware `journeyFor(tenure, opts)` node list.  Using the same
 * SSOT here keeps My Active Deals in lockstep with the Deal Room's
 * "3 of 7 key stages complete · 43%" readout, replacing the coarse
 * 9-stage `stageProgress()` fallback that reported 67% when the
 * canonical answer is 43%.  Falls back to `stageProgress` only when
 * the resolver returns an unusable answer (missing tenure + stage). */
// Same overview SSOT as the Deal Rooms list + property summary (server completed_steps).
const canonicalJourney = (deal) => {
  try {
    return resolveDealJourneyProgress({ ...deal, completedSteps: deal?.completedStepsApi || deal?.completedSteps });
  } catch {
    return null;
  }
};
const dealStageLabel = (deal) => canonicalJourney(deal)?.currentStageLabel || stageLabel(deal?.stage);
const dealNextStep = (deal) => (deal?.next_best_action?.label
  ? { title: deal.next_best_action.label, sub: "Open" }
  : nextMilestoneLabel(deal?.stage));

const journeyProgressPct = (deal) => {
  const canonical = canonicalJourney(deal);
  if (canonical && canonical.totalStepCount > 0) return canonical.progressPct;
  try {
    const safeDeal = deal || {};
    const tenure = String(
      safeDeal?.tenure ||
      safeDeal?.property?.tenure ||
      safeDeal?.property_tenure ||
      "freehold",
    ).toLowerCase();
    const opts = optsForDeal(safeDeal);
    const nodes = journeyFor(tenure, opts);
    const total = Array.isArray(nodes) ? nodes.length : 0;
    if (!total) return stageProgress(safeDeal?.stage);
    const idx = activeIndexForDeal(safeDeal, opts);
    if (typeof idx !== "number" || idx < 0) return stageProgress(safeDeal?.stage);
    const s = String(safeDeal?.stage || "").toLowerCase();
    // Terminal — mirror the Deal Room's post-exchange 100% bump.
    if (safeDeal?.completedAt || safeDeal?.completed_at || s.includes("complet")) return 100;
    if (safeDeal?.exchangedAt || safeDeal?.exchanged_at) return Math.min(100, Math.round(((idx + 1) / total) * 100));
    // idx = current stage; every earlier stage is complete → done = idx.
    const done = Math.max(0, Math.min(total, idx));
    return Math.round((done / total) * 100);
  } catch {
    return stageProgress(deal?.stage);
  }
};

const _STEP_CHIP = {
  mortgage: "Mortgage in progress", contract: "Contract in progress", mgmt_pack: "Management Pack",
  bsa: "BSA", enquiries: "Enquiries pending", exchange: "Needs signature", completion: "Exchanged",
};

// Canonical risk rule first (same classifyDeal as Deal Rooms + property summary), then the current step.
const riskChipFor = (deal) => {
  const cls = classifyDeal(deal);
  if (cls.atRisk) return "At risk";
  if (cls.watch) return "Watch";
  const stage = String(deal.stage || "").toLowerCase();
  if (stage.includes("exchanged")) return "Exchanged";
  return _STEP_CHIP[canonicalJourney(deal)?.currentStageId] || "Stable";
};

const dealRiskPriority = (deal, blockerByDeal) => {
  const dBlock = blockerByDeal?.[deal.id];
  const d2e = daysBetween(deal.targetExchange);
  const stage = String(deal.stage || "").toLowerCase();
  if (dBlock?.severity === "blocking") return 0;          // at risk
  if (d2e !== null && d2e >= 0 && d2e <= 7) return 1;     // exchanging soon
  if (dBlock?.severity === "warning") return 2;           // stalled
  if (String(deal.risk || "").toLowerCase() === "high") return 2;
  if (stage.includes("exchanged") || stage.includes("complet")) return 4;
  return 3;                                                // healthy
};

/* ════════════════════════════════════════════════════════════
   Page
   ════════════════════════════════════════════════════════════ */
export default function TodayPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAllDeals, setShowAllDeals] = useState(false);
  const { user } = useAuth();
  const { deals, contacts, findContact } = useChainStore();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/today/sheet`);
      setData(r.data || null);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Could not load today";
      toast.error(typeof msg === "string" ? msg : "Could not load today");
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await load();
      setLoading(false);
    })();
  }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
    toast.success("Refreshed", { duration: 1200 });
  }, [load]);

  /* Quick Action sheet state */
  const [qaSheet, setQaSheet] = useState(null);
  const [blockersPanelOpen, setBlockersPanelOpen] = useState(false);
  const handleQaDone = useCallback(() => { load(); }, [load]);

  /* iter-alignment · P58 — Deep-link helper.  Scrolls the matching
   * page section (by data-testid) into view with a calm smooth
   * transition.  Reuses existing section testids (no new anchors)
   * so the CTA layer stays a pure UX enhancement — no layout /
   * markup changes on the receiving sections. */
  const scrollToSection = useCallback((testid) => {
    if (typeof document === "undefined") return;
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const needs = useMemo(() => data?.needs_attention || [], [data]);
  const followUps = useMemo(() => data?.follow_ups_due || [], [data]);
  // iter-201 — movement is held in a mutable React state slice so
  // actions (Chase, Request, Log update) can OPTIMISTICALLY prepend
  // an event to the rail before the server confirms. Reads + writes
  // converge on this single source; `load()` resets it from the API.
  const [optimisticMovement, setOptimisticMovement] = useState([]);
  const baseMovement = useMemo(() => data?.todays_movement || [], [data]);
  const movement = useMemo(
    () => [...optimisticMovement, ...baseMovement],
    [optimisticMovement, baseMovement],
  );
  // Reset optimistic queue whenever the server returns a fresh sheet.
  useEffect(() => { setOptimisticMovement([]); }, [data?.fetched_at, baseMovement.length]);

  const prependMovement = useCallback((entry) => {
    setOptimisticMovement((prev) => [
      { id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, ...entry },
      ...prev,
    ]);
  }, []);

  const exchangeWeek = useMemo(() => data?.exchange_week || [], [data]);
  const quick = data?.quick_stats || { total_active: 0, needs_attention_count: 0, movement_today: 0, exchanging_this_week: 0 };

  /* Map: deal_id → top blocker (for active table risk chips). */
  const blockerByDeal = useMemo(() => {
    const m = {};
    for (const n of needs) {
      if (!m[n.deal_id]) m[n.deal_id] = n.blocker;
    }
    return m;
  }, [needs]);

  /* Map: deal_id → fee (number) */
  const feeByDeal = useMemo(() => {
    const m = {};
    if (Array.isArray(deals)) {
      for (const d of deals) {
        if (!d?.id) continue;
        // iter-alignment · P0 · Agreed Fee Basis Reconciliation
        // (2026-02-15) — canonical SSOT resolver so percentage
        // deals compute `agreedPrice × feePct` instead of
        // silently returning 0 when the stored `deal.fee` is
        // null (which it is for every percentage deal — Fee
        // Basis Integrity clears it on save).
        const net = resolveAgreedFeeGBP(d);
        m[d.id] = net != null ? net : 0;
      }
    }
    return m;
  }, [deals]);

  /* Map: deal_id → targetExchange iso */
  const tExByDeal = useMemo(() => {
    const m = {};
    if (Array.isArray(deals)) {
      for (const d of deals) {
        if (d?.id && d.targetExchange) m[d.id] = d.targetExchange;
      }
    }
    return m;
  }, [deals]);

  /* KPI derivations */
  const kpis = useMemo(() => {
    // Shared Needs Attention rule (same as Deal Rooms + Sidebar).
    const atRiskDeals = new Set((Array.isArray(deals) ? deals : []).filter(needsAttentionDeal).map((d) => d.id));
    const atRiskSum = [...atRiskDeals].reduce((acc, id) => acc + (feeByDeal[id] || 0), 0);

    // Expected banking — sum of fees for deals exchanging in next 30 days (or already exchanged this month)
    const now = Date.now();
    const horizon = now + 30 * _MS_DAY;
    const monthBackStart = (() => {
      const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime();
    })();
    let expectedBankingSum = 0;
    let expectedCount = 0;
    if (Array.isArray(deals)) {
      for (const d of deals) {
        // iter-alignment · P0 · Agreed Fee Basis Reconciliation
        // (2026-02-15) — read via the SSOT resolver so
        // percentage deals contribute their computed
        // `agreedPrice × feePct` to Expected Banking instead
        // of being silently dropped by the previous
        // `if (!d?.fee) continue` short-circuit.
        const net = resolveAgreedFeeGBP(d);
        if (net == null || net <= 0) continue;
        const stage = String(d.stage || "").toLowerCase();
        if (stage.includes("fallen") || stage.includes("withdrawn")) continue;
        const te = d.targetExchange ? Date.parse(d.targetExchange) : null;
        const inWindow =
          (te && te >= monthBackStart && te <= horizon) ||
          stage.includes("exchanged") || stage.includes("complet");
        if (inWindow) {
          expectedBankingSum += net;
          expectedCount += 1;
        }
      }
    }

    // Exchanging this week fees
    const exchangeWeekFees = exchangeWeek.reduce((acc, c) => acc + (feeByDeal[c.deal_id] || 0), 0);

    // iter-200 — biggest recurring blocker, derived from the same
    // `waitingOn` grouping the rail uses. Surfaces "Seller solicitor
    // affecting 4 deals" style headlines in the briefing strip.
    const blockerGroups = new Map();
    for (const n of needs) {
      const key = ownerGroupLabel(n.blocker.owner_role);
      const v = blockerGroups.get(key) || { key, role: n.blocker.owner_role, deals: 0 };
      v.deals += 1;
      blockerGroups.set(key, v);
    }
    const topBlockerEntry = [...blockerGroups.values()].sort((a, b) => b.deals - a.deals)[0] || null;

    return {
      needsAttention: { count: atRiskDeals.size, items: quick.needs_attention_count ?? needs.length, atRisk: atRiskSum, dealCount: atRiskDeals.size },
      updatesToday:   { count: quick.movement_today, progressed: Math.min(quick.movement_today, (Array.isArray(deals) ? deals.length : 0)) },
      exchangeWeek:   { count: exchangeWeek.length, fees: exchangeWeekFees },
      expectedBanking:{ amount: expectedBankingSum, count: expectedCount },
      atRisk:         { amount: atRiskSum, dealCount: atRiskDeals.size },
      biggestBlocker: { label: topBlockerEntry?.key || null, deals: topBlockerEntry?.deals || 0 },
    };
  }, [needs, exchangeWeek, deals, feeByDeal, quick]);

  /* Waiting-on grouping from needs_attention. */
  const waitingOn = useMemo(() => {
    const groups = new Map();
    for (const n of needs) {
      const key = ownerGroupLabel(n.blocker.owner_role);
      const entry = groups.get(key) || { key, role: n.blocker.owner_role, deals: 0, ageSum: 0 };
      entry.deals += 1;
      entry.ageSum += Number(n.blocker.age_days || 0);
      groups.set(key, entry);
    }
    return [...groups.values()]
      .map((g) => ({ ...g, avg: g.deals ? Math.round(g.ageSum / g.deals * 10) / 10 : 0 }))
      .sort((a, b) => b.deals - a.deals);
  }, [needs]);

  /* Operational insight one-liner */
  const insight = useMemo(() => {
    if (!needs.length) {
      return "All transactions are calm. No operational blockers detected right now.";
    }
    const top = waitingOn[0];
    const sameRole = top && top.deals >= 2
      ? `${top.deals} deals are waiting on the same ${top.key.toLowerCase()}. `
      : "";
    // iter-alignment · P0 · Command Centre Data Reconciliation
    // (2026-02-28) — drop the "Average response time: 0.0 days"
    // sentence entirely when the blockers were raised today (a
    // reseed / fresh detection).  Surfacing 0.0 days reads as a
    // broken metric to the pilot operator.
    const avg = top && top.deals >= 2 && top.avg >= 0.5
      ? `Average response time: ${top.avg.toFixed(1)} days. `
      : "";
    const exch = kpis.exchangeWeek.count
      ? `${kpis.exchangeWeek.count} transaction${kpis.exchangeWeek.count === 1 ? "" : "s"} likely to exchange this week if actions completed.`
      : "Maintain momentum across active deals to protect this week's exchanges.";
    return `${sameRole}${avg}${exch}`;
  }, [needs, waitingOn, kpis]);

  /* iter-alignment · P57 — Contextual CTA for the Operational
   * Insight bar.  Replaces the generic "View report" with an
   * action-oriented label derived from the operator's top
   * blocker group ("Chase 3 management packs" · "Chase 4 seller
   * solicitors").  Falls back to "View report" when there is no
   * pluralisable top blocker (calm state or edge cases). */
  const insightCta = useMemo(() => {
    if (!needs.length) return "View report";
    const top = waitingOn[0];
    if (!top || !top.deals || top.deals < 1) return "View report";
    const raw = String(top.key || "").trim();
    if (!raw) return "View report";
    const noun = top.deals === 1 ? raw : `${raw}s`;
    return `Chase ${top.deals} ${noun.toLowerCase()}`;
  }, [needs, waitingOn]);

  /* iter-alignment · P57 — Today's Progress strip.  Derives
   * `completed` vs `total` from the movement counter (updates
   * logged today) and the outstanding needs — a proxy for "how
   * many of today's priorities have I moved forward?".  Slim
   * inline strip only; no dedicated backend metric. */
  const progressStats = useMemo(() => {
    const done = Number(quick?.movement_today || 0);
    const total = done + needs.length;
    return { completed: done, total };
  }, [quick, needs]);

  /* Active deals sorted by operational priority.
     iter-alignment · P0 · Command Centre · Final Pilot QA
     (2026-03-02) — filter now delegates to the shared
     `isActiveDeal` SSOT so this list reconciles exactly with
     the sidebar "N Active" chip and the backend's
     `quick_stats.total_active` count.  Previously only
     `stage === "fallen_through"` was excluded, which let a
     `Completed / stage=completed` deal inflate this count and
     drove the reported 16-vs-15 mismatch. */
  const activeDeals = useMemo(() => {
    if (!Array.isArray(deals)) return [];
    return deals
      .filter(isActiveDeal)
      .slice()
      .sort((a, b) => {
        const pa = dealRiskPriority(a, blockerByDeal);
        const pb = dealRiskPriority(b, blockerByDeal);
        if (pa !== pb) return pa - pb;
        const da = daysBetween(a.targetExchange);
        const db = daysBetween(b.targetExchange);
        return (da === null ? 99999 : da) - (db === null ? 99999 : db);
      });
  }, [deals, blockerByDeal]);

  const firstName = (user?.full_name || user?.name || "")
    .split(/\s+/)[0] || (user?.email || "").split("@")[0] || "there";

  /* iter-201 — Scroll memory between Today ↔ Deal Room.
   *
   * Persist the desktop main's scroll offset in sessionStorage so
   * "Open deal room" → back navigation lands the operator exactly
   * where they were. Calm operational continuity, never blocking. */
  useEffect(() => {
    if (loading) return;
    const key = "today.scrollY";
    const stored = Number(sessionStorage.getItem(key) || 0);
    if (stored > 0) {
      // Restore on next paint so the layout has rendered.
      requestAnimationFrame(() => window.scrollTo(0, stored));
    }
    const onScroll = () => {
      sessionStorage.setItem(key, String(window.scrollY || 0));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [loading]);

  return (
    <div className="min-h-screen" style={{ background: "#F7F7FB" }}>
      {/* iter-201 — Calm operational micro-feedback. The row-pulse
          class flashes a 1.2 s violet glow after a Chase succeeds so
          the OS visibly acknowledges the action without toast spam. */}
      <style>{`
        @keyframes today-row-pulse {
          0%   { box-shadow: inset 0 0 0 0 rgba(139,92,246,0); }
          25%  { box-shadow: inset 4px 0 0 0 rgba(139,92,246,0.55), 0 0 0 1px rgba(139,92,246,0.18); }
          100% { box-shadow: inset 0 0 0 0 rgba(139,92,246,0); }
        }
        .today-row-pulse {
          animation: today-row-pulse 1200ms ease-out both;
        }
        @media (prefers-reduced-motion: reduce) {
          .today-row-pulse { animation: none; }
        }
      `}</style>
      <Sidebar />

      {/* Mobile shell — Apple-grade operational cockpit (<md).
       *
       * Belt-and-suspenders responsive separation (iter-175): both the
       * `<TodayMobile>` component's internal root carries `md:hidden`
       * AND this outer wrapper does. If either gate ever breaks, the
       * other one keeps the desktop view clean. The desktop `<main>`
       * below uses `hidden md:block` for the matched gate. */}
      <div className="md:hidden" data-testid="today-mobile-shell">
        <TodayMobileV2
          needs={needs}
          needsAttentionCount={quick.needs_attention_count}
          needsAttentionDeals={(Array.isArray(deals) ? deals : []).filter(needsAttentionDeal).length}
          followUps={followUps}
          movement={movement}
          exchangeWeek={exchangeWeek}
          waitingOn={waitingOn}
          firstName={firstName}
          alertsCount={quick.movement_today}
          onRefresh={onRefresh}
          refreshing={refreshing}
          onOpenReview={() => setBlockersPanelOpen(true)}
          onOpenQaSheet={setQaSheet}
          onChanged={load}
        />
      </div>

      <main
        className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen overflow-x-hidden hidden md:block"
        data-testid="today-desktop-shell"
      >
        <div className={`${WORKSPACE_SHELL_CLASS} pb-32`}>
          <MorningAcknowledgement
            firstName={firstName}
            needsAttention={needs.length}
            followUpsDue={followUps.length}
            className="mb-3"
          />
          <Header
            firstName={firstName}
            onRefresh={onRefresh}
            refreshing={refreshing}
            updatesCount={quick.movement_today}
            /* iter-alignment · P0 · Command Centre · Approved Design
               Polish (2026-02-28) — pipe the exchange-this-week count
               through so the subline reads as a live insight, not a
               static tagline. */
            exchangeWeekCount={kpis?.exchangeWeek?.count || 0}
          />

          {loading ? (
            <LoadingState />
          ) : (
            <>
              <KpiStrip
                k={kpis}
                onNavPriorities={() => scrollToSection("today-needs-section")}
                onNavUpdates={() => scrollToSection("panel-movement")}
                onNavExchanges={() => scrollToSection("panel-exchanges")}
                onNavRisk={() => setBlockersPanelOpen(true)}
                onNavBlocker={() => scrollToSection("panel-waiting")}
              />
              <TodaysProgress
                completed={progressStats.completed}
                total={progressStats.total}
              />
              <OperationalInsight
                insight={insight}
                ctaLabel={insightCta}
                onViewReport={() => setBlockersPanelOpen(true)}
              />

              <NeedsAttentionSection
                items={needs}
                feeByDeal={feeByDeal}
                tExByDeal={tExByDeal}
                onChased={load}
                onOptimisticMovement={prependMovement}
                deals={deals}
                contacts={contacts}
                currentUserId={user?.id}
              />

              {/* iter-200 — Operational Intelligence Rail moves
                  directly below the Operational Priority Queue.
                  My Active Deals becomes workspace-continuation
                  rather than the dashboard. */}
              <LowerGrid
                exchangeWeek={exchangeWeek}
                feeByDeal={feeByDeal}
                waitingOn={waitingOn}
                movement={movement}
              />

              <MyActiveDealsSection
                deals={activeDeals}
                blockerByDeal={blockerByDeal}
                findContact={findContact}
                showAll={showAllDeals}
                onToggleAll={() => setShowAllDeals((v) => !v)}
              />
            </>
          )}
        </div>
      </main>

      <QuickActionsDock onOpen={setQaSheet} />

      <OperationalBlockersPanel
        open={blockersPanelOpen}
        onClose={() => setBlockersPanelOpen(false)}
        needsAttention={needs}
        feeByDeal={feeByDeal}
        tExByDeal={tExByDeal}
        deals={deals}
        onActed={load}
      />

      {/* Quick Action operational sheets */}
      <ChaseSolicitorSheet
        open={qaSheet === "chase"} mode="chase"
        onClose={() => setQaSheet(null)}
        todayData={data} onDone={handleQaDone}
      />
      <ChaseSolicitorSheet
        open={qaSheet === "request"} mode="request"
        onClose={() => setQaSheet(null)}
        todayData={data} onDone={handleQaDone}
      />
      <SendSellerUpdateSheet
        open={qaSheet === "seller"}
        onClose={() => setQaSheet(null)}
        deals={deals} onDone={handleQaDone}
      />
      <UpdateMilestoneSheet
        open={qaSheet === "milestone"}
        onClose={() => setQaSheet(null)}
        deals={deals} onDone={handleQaDone}
      />
      <AddNoteSheet
        open={qaSheet === "note"}
        onClose={() => setQaSheet(null)}
        deals={deals} onDone={handleQaDone}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Header
   ════════════════════════════════════════════════════════════ */
function Header({ firstName, onRefresh, refreshing, updatesCount, exchangeWeekCount }) {
  const nav = useNavigate();
  // iter-alignment · P0 · ChainFlow · Unified Page Header
  // (2026-03-01) — Command Centre now uses the shared PageHeader
  // primitive so navigating between primary pages keeps the icon,
  // title and description visually anchored to the same X/Y.
  return (
    <div data-testid="today-header">
      <PageHeader
        icon={LayoutDashboardIcon}
        title="Flow Centre"
        description={
          typeof exchangeWeekCount === "number" && exchangeWeekCount > 0
            ? `${exchangeWeekCount} ${exchangeWeekCount === 1 ? "exchange is" : "exchanges are"} within reach this week if today's priorities are completed.`
            : "Ranked by conversation momentum · focus on the transactions moving today."
        }
        testid="today-page-header"
        titleTestid="today-title"
        descriptionTestid="today-subline"
        actions={(
          <>
            <IconBtn
              label="Refresh"
              onClick={onRefresh}
              testid="today-refresh"
            >
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={13} />}
          </IconBtn>
          <Link
            to="/notifications"
            aria-label="Notifications"
            className="relative h-9 w-9 rounded-xl bg-white border border-slate-200/80 text-slate-500 hover:text-slate-900 inline-flex items-center justify-center transition-colors"
            data-testid="today-notifications"
          >
            <Bell size={13} />
            {updatesCount > 0 && (
              <span
                className="absolute -top-1 -right-1 h-[16px] min-w-[16px] px-1 rounded-full text-[9px] font-bold text-white inline-flex items-center justify-center"
                style={{ background: "#8B5CF6" }}
              >
                {updatesCount > 99 ? "99+" : updatesCount}
              </span>
            )}
          </Link>
          <GlobalSearch />
          <DateSelector />
          <button
            type="button"
            onClick={() => nav("/deal-rooms?new=1")}
            className="h-9 px-3.5 rounded-xl text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition-all hover:brightness-110"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              boxShadow: "0 8px 22px -10px rgba(99,102,241,0.55)",
            }}
            data-testid="today-new-deal"
          >
            <Plus size={13} strokeWidth={2.4} />
            New deal
          </button>
          </>
        )}
      />

      <div className="mt-3 md:hidden">
        <DateSelector />
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="h-9 w-9 rounded-xl bg-white border border-slate-200/80 text-slate-500 hover:text-slate-900 inline-flex items-center justify-center transition-colors"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function GlobalSearch() {
  const nav = useNavigate();
  return (
    <button
      type="button"
      onClick={() => nav("/contacts")}
      className="hidden md:inline-flex h-9 px-3 rounded-xl bg-white border border-slate-200/80 text-slate-500 hover:text-slate-900 items-center gap-2 transition-colors"
      data-testid="today-search"
    >
      <Search size={13} />
      <span className="text-[12px] text-slate-400">Search deals, parties, notes…</span>
      <kbd className="ml-3 text-[10px] font-semibold text-slate-400 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 leading-none">⌘K</kbd>
    </button>
  );
}

function DateSelector() {
  return (
    <button
      type="button"
      className="h-9 px-3 rounded-xl bg-white border border-slate-200/80 text-[12px] font-semibold text-slate-600 inline-flex items-center gap-2 hover:text-slate-900 transition-colors"
      data-testid="today-date"
    >
      <Calendar size={12} className="text-slate-400" />
      <span className="tabular-nums">Today · {todayShort()}</span>
      <ChevronDown size={12} className="text-slate-400" />
    </button>
  );
}

/* ════════════════════════════════════════════════════════════
   KPI Strip — iter-200 five operational briefing signals
   ════════════════════════════════════════════════════════════ */
function KpiStrip({ k, onNavPriorities, onNavUpdates, onNavExchanges, onNavRisk, onNavBlocker }) {
  /* iter-alignment · P57 — Hide "Revenue at risk" when the value is
   * £0 (no risk to surface).  The grid column count softens so the
   * remaining KPI cards still balance across breakpoints. */
  const showAtRisk = Number(k?.atRisk?.amount || 0) > 0;
  const cardCount = showAtRisk ? 5 : 4;
  const xlColsClass = cardCount === 5 ? "xl:grid-cols-5" : "xl:grid-cols-4";
  /* iter-alignment · P58 — Contextual footer CTAs on every KPI card.
   * Copy is action-oriented (Complete / Review / Confirm / Resolve /
   * Chase) and the count / amount is pulled from the SAME SSOT the
   * card headline already uses — no duplicated business logic.  CTAs
   * hide when nothing is actionable (count === 0); the Revenue-at-
   * Risk card is already hidden entirely at £0 by the outer strip. */
  return (
    <div
      className={`grid grid-cols-2 md:grid-cols-3 ${xlColsClass} gap-4 mt-5`}
      data-testid="today-kpis"
      data-atrisk-visible={showAtRisk ? "true" : "false"}
    >
      <KpiCard
        emphasis="strong"
        accent="#EF4444"
        icon={AlertCircle}
        iconBg="rgba(239,68,68,0.10)"
        iconFg="#B91C1C"
        value={k.needsAttention.count}
        label="Needs Attention"
        sub1={`${k.needsAttention.items} open item${k.needsAttention.items === 1 ? "" : "s"}`}
        testid="kpi-needs"
        cta={{
          label: `Complete ${k.needsAttention.items}`,
          ariaLabel: `Complete ${k.needsAttention.count} priorities`,
          count: k.needsAttention.count,
          onClick: onNavPriorities,
          testid: "kpi-needs-cta",
        }}
      />
      <KpiCard
        accent="#10B981"
        icon={CheckCircle2}
        iconBg="rgba(16,185,129,0.10)"
        iconFg="#047857"
        value={k.updatesToday.count}
        label="Updates since yesterday"
        sub1={`${k.updatesToday.count} operational update${k.updatesToday.count === 1 ? "" : "s"}`}
        testid="kpi-updates"
        cta={{
          label: `Review ${k.updatesToday.count}`,
          ariaLabel: `Review ${k.updatesToday.count} updates`,
          count: k.updatesToday.count,
          onClick: onNavUpdates,
          testid: "kpi-updates-cta",
        }}
      />
      <KpiCard
        accent="#6366F1"
        icon={ArrowRightLeft}
        iconBg="rgba(99,102,241,0.10)"
        iconFg="#4338CA"
        value={k.exchangeWeek.count}
        label="Exchanges this week"
        sub1={`${k.exchangeWeek.count} expected exchange${k.exchangeWeek.count === 1 ? "" : "s"}`}
        testid="kpi-exchange"
        cta={{
          label: `Confirm ${k.exchangeWeek.count}`,
          ariaLabel: `Confirm ${k.exchangeWeek.count} exchanges this week`,
          count: k.exchangeWeek.count,
          onClick: onNavExchanges,
          testid: "kpi-exchange-cta",
        }}
      />
      {showAtRisk && (
        <KpiCard
          emphasis="strong"
          accent="#3B82F6"
          icon={PoundSterling}
          iconBg="rgba(59,130,246,0.10)"
          iconFg="#1D4ED8"
          value={fmtGBP(k.atRisk.amount)}
          valueMono
          label="Revenue at risk"
          sub1={`Across ${k.atRisk.dealCount} deal${k.atRisk.dealCount === 1 ? "" : "s"}`}
          testid="kpi-atrisk"
          cta={{
            label: `Resolve ${fmtGBP(k.atRisk.amount)}`,
            ariaLabel: `Resolve revenue at risk of ${fmtGBP(k.atRisk.amount)}`,
            count: k.atRisk.amount, // > 0 by construction (outer guard)
            onClick: onNavRisk,
            testid: "kpi-atrisk-cta",
          }}
        />
      )}
      <KpiCard
        emphasis="strong"
        accent="#F97316"
        icon={UserCircle2}
        iconBg="rgba(249,115,22,0.10)"
        iconFg="#C2410C"
        value={k.biggestBlocker.label || "Calm"}
        valueIsText
        label="Biggest blocker"
        sub1={k.biggestBlocker.deals
          ? `Affecting ${k.biggestBlocker.deals} deal${k.biggestBlocker.deals === 1 ? "" : "s"}`
          : "No recurring blocker"}
        testid="kpi-blocker"
        cta={{
          label: `Chase ${k.biggestBlocker.deals || 0}`,
          ariaLabel: `Chase ${k.biggestBlocker.deals || 0} deals blocked on ${k.biggestBlocker.label || "resolution"}`,
          count: k.biggestBlocker.deals || 0,
          onClick: onNavBlocker,
          testid: "kpi-blocker-cta",
        }}
      />
    </div>
  );
}

function KpiCard({
  emphasis, accent, icon: Icon, iconBg, iconFg,
  value, valueMono, valueIsText, label, sub1, testid,
  cta,
}) {
  const strong = emphasis === "strong";
  /* iter-alignment · P58 — Contextual footer CTA.  Subtle brand-
   * coloured text-link (NOT a button) rendered at the bottom of the
   * card.  Hidden when nothing is actionable (`cta.count === 0`) so
   * calm cards stay calm.  KPI value keeps the visual focus — CTA
   * ink weight sits well below it. */
  const showCta = !!(cta && cta.label && (cta.count === undefined || cta.count > 0));
  /* iter-alignment · P0 · Command Centre · KPI Card · Pass 3
   * (2026-02-28)
   * ----------------------------------------------------------------
   * Smallest-safe polish per `/app/design_guidelines.json`:
   *   • Padding `px-4 py-3.5` → `px-5 py-4`  · calmer breathing that
   *     rhymes with the Priority Row + Lower Shelf card rhythm.
   *   • Icon → content gap `gap-2.5` → `gap-3`  · matches the same
   *     16px/12px rhythm the rest of the shelf uses.
   *   • Ring opacity `226,232,240,0.55` → `226,232,240,0.75`  ·
   *     aligns the KPI ring with the Lower-Shelf `border-slate-100/70`
   *     divider so every card reads with the same weight of edge.
   *   • Value → Label gap `mt-1.5` → `mt-1`  · tightens the big-
   *     number-plus-label block per the design's "tight block"
   *     directive.
   *   • Label → Sub1 gap `mt-1.5` → `mt-1`  · keeps the descriptor
   *     tightly coupled to the label.
   *   • Sub1 → CTA gap `mt-2` → `mt-2.5`  · a whisper more breathing
   *     before the coloured action link, so the CTA reads as "at the
   *     bottom" rather than glued to the descriptor.
   * DOM, testids, business logic, aria-labels, hover behaviour and
   * the `strong` emphasis wash are all preserved verbatim.
   */
  return (
    <div
      className="rounded-2xl bg-white/95 px-5 py-4 relative overflow-hidden cf-card-hover"
      style={{
        // iter-alignment · P57 — Softer KPI card treatment.
        // Rings + shadows are dropped a notch so the priorities +
        // communications tables stay the visual focus of the page.
        boxShadow: strong
          ? `0 1px 1px rgba(15,23,42,0.02), 0 0 0 1px ${accent}14`
          : "0 1px 1px rgba(15,23,42,0.02), 0 0 0 1px rgba(226,232,240,0.75)",
      }}
      data-testid={testid}
    >
      {strong && (
        <div
          aria-hidden
          className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-[0.11] blur-2xl"
          style={{ background: accent }}
        />
      )}
      <div className="flex items-start gap-3">
        {Icon && (
          <span
            className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: iconBg, color: iconFg }}
          >
            <Icon size={14} strokeWidth={2.4} />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p
            className={`${
              valueIsText
                ? "text-[15px] md:text-[16px] font-bold leading-[1.15] tracking-[-0.01em] truncate"
                : valueMono
                  ? "text-[22px] md:text-[24px] font-bold leading-none tabular-nums tracking-[-0.02em]"
                  : "text-[26px] md:text-[30px] font-bold leading-none tabular-nums tracking-[-0.02em]"
            }`}
            style={{ color: accent }}
          >
            {value}
          </p>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-500 mt-1">
            {label}
          </p>
          <p className="text-[11.5px] text-slate-500 mt-1 truncate">{sub1}</p>
          {showCta && (
            <button
              type="button"
              onClick={cta.onClick}
              className="mt-2.5 inline-flex items-center gap-1 text-[11.5px] font-semibold tabular-nums hover:brightness-90 hover:underline underline-offset-2 focus:outline-none focus-visible:underline transition-colors cf-press"
              style={{ color: accent }}
              data-testid={cta.testid || `${testid}-cta`}
              data-cta-count={cta.count ?? undefined}
              aria-label={cta.ariaLabel || cta.label}
            >
              <span>{cta.label}</span>
              <ArrowRight size={11} strokeWidth={2.4} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Operational Insight
   ════════════════════════════════════════════════════════════ */
function OperationalInsight({ insight, ctaLabel, onViewReport }) {
  /* iter-alignment · P57 — CTA label defaults to the operator's
   * top blocker action (e.g. "Chase 3 management packs") instead
   * of the generic "View report".  Passed from the parent so the
   * copy tracks whatever `waitingOn[0]` currently surfaces. */
  const cta = ctaLabel || "View report";
  return (
    <div
      className="mt-4 rounded-2xl border px-5 py-3.5 flex items-center gap-4 relative overflow-hidden"
      style={{
        // iter-alignment · P57 — Softer treatment (calmer background,
        // thinner border, whisper of shadow) so the tables below own
        // the visual weight.
        background: "linear-gradient(135deg, rgba(139,92,246,0.035) 0%, rgba(99,102,241,0.02) 100%)",
        borderColor: "rgba(139,92,246,0.12)",
        boxShadow: "0 1px 1px rgba(15,23,42,0.02)",
      }}
      data-testid="today-insight"
    >
      <span
        className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
        style={{ background: "rgba(139,92,246,0.10)", color: "#6D28D9" }}
      >
        <Sparkles size={15} strokeWidth={2.2} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">
          Operational insight
        </p>
        <p className="text-[13px] text-slate-700 leading-snug mt-0.5">
          {insight}
        </p>
      </div>
      <button
        type="button"
        onClick={onViewReport}
        className="hidden md:inline-flex h-8 px-3 rounded-lg text-[12px] font-semibold items-center gap-1.5 shrink-0 transition-colors hover:brightness-105"
        style={{ color: "#6D28D9", background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.18)" }}
        data-testid="today-insight-view-report"
      >
        {cta} <ChevronRight size={12} />
      </button>
      <button
        type="button"
        onClick={onViewReport}
        aria-label={cta}
        className="md:hidden h-8 w-8 rounded-lg inline-flex items-center justify-center shrink-0"
        style={{ color: "#6D28D9", background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.18)" }}
        data-testid="today-insight-view-report-mobile"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Today's Progress · slim inline strip (iter-alignment · P57)
   ────────────────────────────────────────────────────────────
   A whisper-weight bar rendered directly beneath the KPI cards
   so operators can scan today's completion at a glance without
   pulling focus from the primary priorities / communications
   tables.  Deliberately NOT a card — no border, no shadow.
   ═══════════════════════════════════════════════════════════ */
function TodaysProgress({ completed, total }) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const safeDone  = Math.max(0, Math.min(safeTotal, Number(completed) || 0));
  const pct = safeTotal ? Math.round((safeDone / safeTotal) * 100) : 0;
  if (safeTotal === 0) return null;
  return (
    <div
      className="mt-3 flex items-center gap-3 px-1"
      data-testid="today-progress"
      data-completed={safeDone}
      data-total={safeTotal}
    >
      <span
        aria-hidden
        className="w-4 h-4 rounded-full inline-flex items-center justify-center"
        style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}
      >
        <CheckCircle2 size={10} strokeWidth={2.6} />
      </span>
      <p className="text-[12px] font-semibold text-slate-700 leading-none">
        Today’s Progress
      </p>
      <span className="text-slate-300 leading-none">·</span>
      <p className="text-[12px] text-slate-500 leading-none tabular-nums">
        {safeDone} of {safeTotal} priorities completed
      </p>
      <span className="text-slate-300 leading-none">·</span>
      <p className="text-[12px] font-semibold text-slate-700 leading-none tabular-nums">
        {pct}%
      </p>
      <div className="flex-1 min-w-[80px] h-[6px] rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{ width: `${pct}%`, background: "linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)" }}
          data-testid="today-progress-bar"
        />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Needs Attention
   ════════════════════════════════════════════════════════════ */
function SectionHeader({ title, count, accent = "#94A3B8", subtitle, right, testid }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3" data-testid={testid}>
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-[16px] md:text-[18px] font-bold tracking-[-0.01em] text-slate-900">{title}</h2>
          {count !== undefined && (
            <span
              className="text-[10.5px] font-bold tabular-nums px-1.5 h-[19px] rounded-full inline-flex items-center"
              style={{ background: `${accent}1a`, color: accent }}
            >
              {count}
            </span>
          )}
        </div>
        {subtitle && <p className="text-[12px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Operational Priority Queue — iter-200 hero queue.
   P3 · Command Centre — swapped in place for the new
   Highest-Impact Conversations workspace.  The old rows still
   live in `LegacyNeedsAttentionSection` and render when the
   operator hits `/today?legacy=1` (pilot rollback safety net).
   ════════════════════════════════════════════════════════════ */
import HighestImpactConversations from "../components/today/HighestImpactConversations";

function NeedsAttentionSection(props) {
  const legacy = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("legacy") === "1";
  if (legacy) return <LegacyNeedsAttentionSection {...props} />;
  return (
    <HighestImpactConversations
      items={props.items}
      deals={props.deals || []}
      contacts={props.contacts || []}
      feeByDeal={props.feeByDeal}
      tExByDeal={props.tExByDeal}
      currentUserId={props.currentUserId}
      onEmpty={() => <LegacyNeedsAttentionSection {...props} />}
    />
  );
}

function LegacyNeedsAttentionSection({ items, feeByDeal, tExByDeal, onChased, onOptimisticMovement }) {
  if (!items.length) {
    return (
      <section className="mt-8" data-testid="today-needs-section">
        <SectionHeader
          title="Operational Priority Queue"
          subtitle="Deals that need your action to keep moving."
          count={0}
          accent="#94A3B8"
        />
        <EmptyCard
          icon={CheckCircle2}
          iconBg="rgba(16,185,129,0.10)"
          iconFg="#047857"
          title="Nothing needs attention"
          subtitle="All active transactions look calm. Enjoy the breathing room."
        />
      </section>
    );
  }
  return (
    <section className="mt-8" data-testid="today-needs-section">
      <SectionHeader
        title="Operational Priority Queue"
        subtitle="Deals that need your action to keep moving."
        count={items.length}
        accent="#EF4444"
        right={(
          <Link
            to="/sales-progression?filter=needs-attention&sort=priority"
            className="text-[12px] font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="today-needs-view-all"
          >
            View all {items.length} <ChevronRight size={12} />
          </Link>
        )}
      />
      <div
        className="rounded-2xl bg-white overflow-hidden"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 0 0 1px rgba(226,232,240,0.7)" }}
      >
        {items.slice(0, 6).map((item, i) => (
          <NeedsAttentionRow
            key={`${item.deal_id}-${item.blocker.id}-${i}`}
            item={item}
            index={i}
            isFirst={i === 0}
            isLast={i === items.slice(0, 6).length - 1}
            fee={feeByDeal[item.deal_id]}
            targetExchange={tExByDeal[item.deal_id]}
            onChased={onChased}
            onOptimisticMovement={onOptimisticMovement}
          />
        ))}
      </div>
      <div className="mt-3 text-center">
        <Link
          to="/sales-progression?filter=needs-attention&sort=priority"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-violet-700 hover:text-violet-900"
          data-testid="today-priority-queue-viewall"
        >
          View all {items.length} deals needing attention <ChevronRight size={12} />
        </Link>
      </div>
    </section>
  );
}

function NeedsAttentionRow({ item, index, isFirst, isLast, fee, targetExchange, onChased, onOptimisticMovement }) {
  const tone = SEVERITY[item.blocker.severity] || SEVERITY.info;
  const nav = useNavigate();
  const [sending, setSending] = useState(false);
  const [chasedAt, setChasedAt] = useState(item.blocker.last_chased_at || null);
  // iter-201 — calm row pulse after a successful chase. Reads as
  // operational feedback (the OS heard you) without toast spam.
  const [pulsing, setPulsing] = useState(false);

  const onSendChase = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (sending) return;
    // Optimistic UI — flip the badge + prepend a movement entry the
    // moment the click lands. Server confirmation runs in parallel.
    const optimisticAt = new Date().toISOString();
    setChasedAt(optimisticAt);
    setPulsing(true);
    onOptimisticMovement?.({
      verb: "Chase sent",
      deal_id: item.deal_id,
      deal_name: item.property_address || item.deal_name,
      created_at: optimisticAt,
    });
    setSending(true);
    try {
      const { data } = await api.post(`/api/deals/${item.deal_id}/awaiting/chase`, {
        item_id: item.blocker.id,
        title: item.blocker.title,
        owner: item.blocker.owner_role || null,
        days_waiting: item.blocker.age_days || 0,
        send_email: false,
      });
      // Reconcile timestamp with what the server returned.
      setChasedAt(data?.at || optimisticAt);
      invalidateDealActivity(item.deal_id);
      // Background refresh — never block the UI.
      onChased?.();
    } catch (err) {
      // Roll back the optimistic state on failure. No toast spam —
      // we surface a calm inline error instead.
      setChasedAt(item.blocker.last_chased_at || null);
      const status = err?.response?.status;
      if (status === 429) toast("Just chased — give it a moment.");
      else toast.error("Could not send chase");
    } finally {
      setSending(false);
      // Hold the pulse for 1.2 s — long enough to feel, short
      // enough to disappear.
      setTimeout(() => setPulsing(false), 1200);
    }
  }, [sending, item, onChased, onOptimisticMovement]);

  const d2e = daysBetween(targetExchange);
  const teLabel = targetExchange
    ? `${fmtDateShort(targetExchange)} ${d2e !== null ? (d2e < 0 ? `(${Math.abs(d2e)}d overdue)` : d2e === 0 ? "(today)" : `(in ${d2e} day${d2e === 1 ? "" : "s"})`) : ""}`
    : "—";

  const stagePill = stageLabel(item.deal_stage || "exchange") || "Exchange ready";
  const ownerSilenceLabel = `${ownerLabel(item.blocker.owner_role)} silent`;
  const ageDays = item.blocker.age_days || 0;
  const latestUpdate = item.blocker.last_update || `No update from ${ownerLabel(item.blocker.owner_role).toLowerCase()}`;
  const latestUpdateAge = ageDays >= 1 ? `${ageDays} day${ageDays === 1 ? "" : "s"} ago` : "Today";

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => nav(`/deal-rooms/${item.deal_id}`)}
      onKeyDown={(e) => { if (e.key === "Enter") nav(`/deal-rooms/${item.deal_id}`); }}
      className={`group relative ${isLast ? "" : "border-b border-slate-100"} transition-colors hover:bg-slate-50/40 cursor-pointer ${pulsing ? "today-row-pulse" : ""}`}
      data-testid={`today-needs-card-${index}`}
      data-pulsing={pulsing ? "1" : "0"}
    >
      {/* Severity rail */}
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ background: tone.rail }}
      />
      <div className="pl-5 pr-4 py-4 grid grid-cols-[44px_minmax(0,1.5fr)_minmax(0,0.85fr)_minmax(0,1fr)_auto] items-center gap-4">
        {/* Priority badge */}
        <span
          className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[13px] font-bold tabular-nums"
          style={{ background: tone.iconBg, color: tone.icon }}
          data-testid={`today-priority-${index}`}
        >
          {index + 1}
        </span>

        {/* Property + stage pill + blocker + silence indicator */}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p
              className="text-[14px] font-semibold text-slate-900 truncate max-w-[280px] tracking-[-0.005em]"
              data-testid={`today-needs-property-${index}`}
            >
              {item.property_address}
            </p>
            <span
              className="inline-flex items-center px-2 h-[20px] rounded-md text-[10.5px] font-semibold"
              style={{ background: "rgba(139,92,246,0.10)", color: "#6D28D9" }}
              data-testid={`today-stage-pill-${index}`}
            >
              {stagePill}
            </span>
          </div>
          <p className="text-[12.5px] text-slate-700 leading-snug mt-1 truncate">
            {item.blocker.title}
          </p>
          <p className="text-[11px] mt-1 truncate inline-flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: tone.rail }} />
            <span className="text-slate-500">{ownerSilenceLabel}</span>
            <span className="text-slate-300">·</span>
            <span className="tabular-nums font-semibold" style={{ color: tone.fg }}>{ageDays}d</span>
          </p>
        </div>

        {/* Target exchange + Fee at risk */}
        <div className="min-w-0">
          <p className="text-[10px] uppercase font-bold tracking-[0.12em] text-slate-400">Target exchange</p>
          <p
            className="text-[13px] font-semibold tabular-nums mt-1 truncate"
            style={{ color: d2e !== null && d2e <= 7 ? tone.fg : "#0F172A" }}
          >
            {teLabel}
          </p>
          <p className="text-[10px] uppercase font-bold tracking-[0.12em] text-slate-400 mt-2.5">Fee at risk</p>
          <p className="text-[13px] font-semibold tabular-nums mt-1" style={{ color: tone.fg }}>
            {fmtGBP(fee)}
          </p>
        </div>

        {/* Latest update + timestamp */}
        <div className="min-w-0">
          <p className="text-[10px] uppercase font-bold tracking-[0.12em] text-slate-400">Latest update</p>
          <p className="text-[12.5px] text-slate-700 mt-1 leading-snug line-clamp-2">{latestUpdate}</p>
          <p className="text-[11px] text-slate-400 mt-1 tabular-nums">{latestUpdateAge}</p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex items-center gap-1.5">
            {chasedAt ? (
              <span
                className="h-[34px] px-3.5 rounded-lg inline-flex items-center gap-1.5 text-[12px] font-semibold"
                style={{ background: "rgba(16,185,129,0.10)", color: "#047857" }}
                data-testid={`today-needs-chased-${index}`}
              >
                <Check size={12} strokeWidth={2.6} />
                Chased {relTime(chasedAt)}
              </span>
            ) : (
              <button
                type="button"
                onClick={onSendChase}
                disabled={sending}
                className="h-[34px] px-3.5 rounded-lg text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition-all disabled:opacity-60"
                style={{
                  background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
                  boxShadow: "0 6px 16px -8px rgba(99,102,241,0.55)",
                }}
                data-testid={`today-needs-chase-${index}`}
              >
                {sending ? <Loader2 size={11} className="animate-spin" /> : <MessageSquare size={11} strokeWidth={2.4} />}
                Chase
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); /* placeholder for overflow */ }}
              className="h-[34px] w-8 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-700 inline-flex items-center justify-center"
              data-testid={`today-needs-overflow-${index}`}
              aria-label="More"
            >
              <ChevronDown size={12} />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); nav(`/deal-rooms/${item.deal_id}`); }}
              className="h-[30px] px-3 rounded-lg bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
              data-testid={`today-needs-open-${index}`}
            >
              <FileText size={11} />
              Open deal room
            </button>
            {/* iter-P65 · Contextual Request Document.  Only shows
                when the blocker is doc-related, deep-linking into
                the canonical Deal Room drawer with the correct
                template pre-seeded (heuristic: reuse the
                `isDocRequestRelevant` helper on the blocker title). */}
            {isDocRequestRelevant(item.blocker.title) && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const url = buildRequestDocUrl(item.deal_id);
                  if (url) nav(url);
                }}
                className="h-[30px] px-3 rounded-lg text-[12px] font-semibold text-white inline-flex items-center gap-1.5"
                style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" }}
                data-testid={`today-needs-request-doc-${index}`}
                title="Request the document via the canonical drawer"
              >
                <Send size={11} strokeWidth={2.4} /> Request
              </button>
            )}
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className="h-[30px] w-8 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-700 inline-flex items-center justify-center"
              aria-label="More actions"
            >
              <MoreHorizontal size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function nextActionLabel(item) {
  const title = (item.blocker.title || "").toLowerCase();
  const days = item.blocker.age_days || 0;
  const stale = days >= 5 ? " — escalate" : days >= 3 ? " — second chase" : "";
  if (title.includes("contract") || title.includes("signed"))
    return `Chase seller solicitor for signed contract${stale}.`;
  if (title.includes("enquir"))
    return `Press solicitor for outstanding enquiry response${stale}.`;
  if (title.includes("mortgage"))
    return `Chase broker for mortgage offer confirmation${stale}.`;
  if (title.includes("search"))
    return `Follow up with searches provider on turnaround${stale}.`;
  if (title.includes("survey"))
    return `Chase surveyor for report delivery${stale}.`;
  if (title.includes("signature"))
    return `Request outstanding signature from counterparty${stale}.`;
  if (title.includes("proof of funds") || title.includes("aml"))
    return `Request proof-of-funds from buyer side${stale}.`;
  if (title.includes("memo") || title.includes("memorandum"))
    return `Issue memorandum of sale to all parties${stale}.`;
  if (title.includes("deposit"))
    return `Confirm deposit funds with buyer solicitor${stale}.`;
  return `Follow up with ${ownerLabel(item.blocker.owner_role).toLowerCase()}${stale}.`;
}

/* ════════════════════════════════════════════════════════════
   My Active Deals — dense board
   ════════════════════════════════════════════════════════════ */
function MyActiveDealsSection({ deals, blockerByDeal, findContact, showAll, onToggleAll }) {
  const visible = showAll ? deals : deals.slice(0, 6);

  if (deals.length === 0) {
    return (
      <section className="mt-8" data-testid="today-active-section">
        <SectionHeader
          title="My active deals"
          subtitle="Live transactions in your pipeline · sorted by priority"
          count={0}
          accent="#475569"
        />
        <EmptyCard
          icon={Briefcase}
          iconBg="rgba(99,102,241,0.10)"
          iconFg="#6366F1"
          title="No active transactions yet"
          subtitle="When you accept an offer, the deal will surface here."
        />
      </section>
    );
  }

  return (
    <section className="mt-8" data-testid="today-active-section">
      <SectionHeader
        title="My active deals"
        subtitle="Live transactions in your pipeline · sorted by priority"
        count={deals.length}
        accent="#475569"
        right={(
          <button
            type="button"
            className="h-8 px-3 rounded-lg bg-white border border-slate-200/80 text-[12px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 transition-colors"
            data-testid="today-active-filter"
          >
            <Filter size={11} />
            Filter
          </button>
        )}
      />

      {/* Desktop table */}
      <div
        className="hidden md:block rounded-2xl bg-white border border-slate-100/70 overflow-hidden"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
      >
        <table className="w-full text-left">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              <th className="px-5 py-3 font-bold">Deal</th>
              <th className="px-3 py-3 font-bold">Parties</th>
              <th className="px-3 py-3 font-bold">Stage & progress</th>
              <th className="px-3 py-3 font-bold">Next milestone</th>
              <th className="px-3 py-3 font-bold">Target exchange</th>
              <th className="px-3 py-3 font-bold">Status</th>
              <th className="px-3 py-3 font-bold pr-5">Updated</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((d, i) => (
              <ActiveDealRow
                key={d.id}
                deal={d}
                index={i}
                blocker={blockerByDeal[d.id]}
                findContact={findContact}
              />
            ))}
          </tbody>
        </table>
        {deals.length > 6 && (
          <button
            type="button"
            onClick={onToggleAll}
            className="w-full py-3 text-center border-t border-slate-100/70 text-[12.5px] font-semibold text-violet-700 hover:bg-violet-50/60 transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid="today-active-view-all"
          >
            {showAll ? "Show fewer deals" : `View all ${deals.length} deals`}
            <ChevronDown size={12} className={showAll ? "rotate-180 transition-transform" : "transition-transform"} />
          </button>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2.5">
        {visible.map((d, i) => (
          <ActiveDealMobile
            key={d.id} deal={d} index={i}
            blocker={blockerByDeal[d.id]} findContact={findContact}
          />
        ))}
        {deals.length > 6 && (
          <button
            type="button"
            onClick={onToggleAll}
            className="w-full h-10 rounded-xl bg-white border border-slate-100/70 text-[12.5px] font-semibold text-violet-700 transition-colors"
          >
            {showAll ? "Show fewer deals" : `View all ${deals.length} deals`}
          </button>
        )}
      </div>
    </section>
  );
}

function ActiveDealRow({ deal, index, blocker, findContact }) {
  const chip = riskChipFor(deal, { [deal.id]: blocker });
  const tone = STATUS_CHIP[chip] || STATUS_CHIP.Stable;
  // iter-alignment · P0 · Command Centre Data Reconciliation
  // (2026-02-28) — the pilot seed populates typed participant rows
  // (`deal.participants`) but leaves the top-level FK columns
  // empty, so buyer / seller avatars were rendering a generic
  // "B"/"S" placeholder even when the canonical contact was
  // present.  Fall back to the participant slice.
  const _partName = (type) => {
    const p = Array.isArray(deal.participants)
      ? deal.participants.find((x) => x.participant_type === type)
      : null;
    return p?.full_name || null;
  };
  const buyer = deal.buyerId ? findContact?.(deal.buyerId) : null;
  const seller = deal.sellerId ? findContact?.(deal.sellerId) : null;
  const bn = buyer?.name || buyer?.full_name || _partName("buyer");
  const sn = seller?.name || seller?.full_name || _partName("seller");
  const d2e = daysBetween(deal.targetExchange);
  const progress = journeyProgressPct(deal);
  const milestone = dealNextStep(deal);
  const progressColor = blocker?.severity === "blocking" ? "#EF4444"
    : (d2e !== null && d2e >= 0 && d2e <= 7) ? "#10B981"
    : progress < 40 ? "#F59E0B" : "#10B981";

  const exchangeTone = d2e !== null && d2e <= 7
    ? (blocker?.severity === "blocking" ? "#B91C1C" : "#C2410C")
    : "#0F172A";

  return (
    <tr
      className="border-t border-slate-100/70 hover:bg-slate-50/50 transition-colors cursor-pointer"
      onClick={() => { window.location.assign(`/deal-rooms/${deal.id}`); }}
      data-testid={`today-active-row-${index}`}
    >
      <td className="px-5 py-[18px]">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
            style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}
          >
            <Home size={14} strokeWidth={2.4} />
          </span>
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-slate-900 truncate max-w-[240px] tracking-[-0.005em]">
              {deal.name || "Untitled deal"}
            </p>
            {deal.agreedPrice && (
              <p className="text-[11.5px] text-slate-500 tabular-nums mt-0.5">
                {fmtGBP(deal.agreedPrice)}
              </p>
            )}
          </div>
        </div>
      </td>

      <td className="px-3 py-[18px]">
        <div className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500">
          <Avatar text={initials(bn) || "B"} />
          <ArrowRight size={11} className="text-slate-300" />
          <Avatar text={initials(sn) || "S"} />
        </div>
        <p className="text-[10.5px] text-slate-400 mt-1">Buyer → Seller</p>
      </td>

      <td className="px-3 py-[18px]">
        <p className="text-[12.5px] font-semibold text-slate-700 truncate max-w-[160px]">
          {dealStageLabel(deal)}
        </p>
        <p className="text-[10.5px] text-slate-400 mt-0.5">{blocker?.title ? truncate(blocker.title, 26) : chip === "At risk" ? "At risk" : "On track"}</p>
        <div className="mt-1.5 flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden max-w-[140px]">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${progress}%`, background: progressColor }}
            />
          </div>
          <span className="text-[10.5px] tabular-nums text-slate-500">{progress}%</span>
        </div>
      </td>

      <td className="px-3 py-[18px]">
        <p className="text-[12.5px] font-medium text-slate-700 truncate max-w-[170px]">{milestone.title}</p>
        <p className="text-[10.5px] text-slate-400 mt-0.5">{milestone.sub}</p>
      </td>

      <td className="px-3 py-[18px]">
        <p className="text-[12.5px] font-semibold tabular-nums" style={{ color: "#0F172A" }}>
          {fmtDateShort(deal.targetExchange) || "—"}
        </p>
        {d2e !== null && (
          <p className="text-[10.5px] tabular-nums mt-0.5" style={{ color: exchangeTone }}>
            {d2e < 0 ? `${Math.abs(d2e)}d overdue` : d2e === 0 ? "today" : `in ${d2e} day${d2e === 1 ? "" : "s"}`}
          </p>
        )}
      </td>

      <td className="px-3 py-[18px]">
        <span
          className="text-[10.5px] font-semibold px-2 h-[22px] rounded inline-flex items-center"
          style={{ background: tone.bg, color: tone.fg }}
          data-testid={`today-active-chip-${index}`}
        >
          {chip}
        </span>
        {blocker?.age_days ? (
          <p className="text-[10.5px] text-slate-400 mt-1 tabular-nums">
            {blocker.age_days}d outstanding
          </p>
        ) : null}
      </td>

      {/* iter-200 — Updated cell with fresh-activity dot. Reads as
          "workspace continuation" cue — never noisy. */}
      <td className="px-3 py-[18px] pr-5">
        <div className="flex items-center gap-2 justify-end">
          <span
            className="text-[11px] text-slate-500 tabular-nums truncate"
            data-testid={`today-active-updated-${index}`}
          >
            {relTime(deal.updatedAt || deal.lastActivityAt || deal.updated_at) || "—"}
          </span>
          {(blocker?.severity === "blocking" || (d2e !== null && d2e !== undefined && d2e <= 3)) && (
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: blocker?.severity === "blocking" ? "#EF4444" : "#F59E0B" }}
              aria-hidden
              title={blocker?.severity === "blocking" ? "Blocking" : "Time pressure"}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

function Avatar({ text }) {
  return (
    <span
      className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold"
      style={{ background: "rgba(99,102,241,0.10)", color: "#4338CA" }}
    >
      {text}
    </span>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function ActiveDealMobile({ deal, index, blocker, findContact }) {
  const chip = riskChipFor(deal, { [deal.id]: blocker });
  const tone = STATUS_CHIP[chip] || STATUS_CHIP.Stable;
  const progress = journeyProgressPct(deal);
  const d2e = daysBetween(deal.targetExchange);
  const _partName = (type) => {
    const p = Array.isArray(deal.participants)
      ? deal.participants.find((x) => x.participant_type === type)
      : null;
    return p?.full_name || null;
  };
  const buyer = deal.buyerId ? findContact?.(deal.buyerId) : null;
  const seller = deal.sellerId ? findContact?.(deal.sellerId) : null;
  const buyerName = buyer?.name || _partName("buyer");
  const sellerName = seller?.name || _partName("seller");
  return (
    <Link
      to={`/deal-rooms/${deal.id}`}
      className="block rounded-2xl bg-white border border-slate-100/70 p-5 hover:border-slate-200 transition-all"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid={`today-active-card-${index}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-slate-900 truncate">{deal.name || "Untitled"}</p>
          {deal.agreedPrice && (
            <p className="text-[11px] text-slate-500 tabular-nums mt-0.5">{fmtGBP(deal.agreedPrice)}</p>
          )}
        </div>
        <span
          className="text-[9.5px] font-bold px-1.5 h-[18px] rounded inline-flex items-center shrink-0"
          style={{ background: tone.bg, color: tone.fg }}
        >
          {chip}
        </span>
      </div>
      <p className="text-[11.5px] text-slate-500 mt-1.5">
        <span className="text-slate-700 font-medium">{dealStageLabel(deal)}</span>
        <span className="text-slate-300 mx-1.5">·</span>
        <span>{dealNextStep(deal).title}</span>
      </p>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${progress}%`, background: blocker?.severity === "blocking" ? "#EF4444" : "#10B981" }}
          />
        </div>
        <span className="text-[10.5px] tabular-nums text-slate-500">{progress}%</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[10.5px]">
        <div className="inline-flex items-center gap-1.5">
          <Avatar text={initials(buyerName) || "B"} />
          <ArrowRight size={10} className="text-slate-300" />
          <Avatar text={initials(sellerName) || "S"} />
        </div>
        <span className="text-slate-500 tabular-nums">
          {fmtDateShort(deal.targetExchange) || "—"}
          {d2e !== null && (
            <span className="text-slate-400"> · {d2e < 0 ? `${Math.abs(d2e)}d overdue` : `in ${d2e}d`}</span>
          )}
        </span>
      </div>
    </Link>
  );
}

/* ════════════════════════════════════════════════════════════
   Lower 3-col grid
   ════════════════════════════════════════════════════════════ */
function LowerGrid({ exchangeWeek, feeByDeal, waitingOn, movement }) {
  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="today-lower-grid">
      <ExchangesThisWeek items={exchangeWeek} feeByDeal={feeByDeal} />
      <WaitingOn items={waitingOn} />
      <TodaysMovement items={movement} />
    </div>
  );
}

function PanelShell({ title, count, accent, subtitle, children, viewAllTo, testid }) {
  /* iter-alignment · P0 · Command Centre · Lower Shelf · Pass 2
   * (2026-02-28)
   * ----------------------------------------------------------------
   * Pass 2 polish per `/app/design_guidelines.json`:
   *   • Unified card padding `p-4 md:p-5` → `p-5` so every lower
   *     shelf card has identical internal breathing across
   *     breakpoints.
   *   • Header bottom rhythm `mb-3` → `mb-4` to match the Priority
   *     Row's calmer breathing room and rhyme with the KPI strip
   *     descriptor gap.
   *   • Softer card border `border-slate-100` → `border-slate-100/70`
   *     mirrors the Priority Row divider polish.
   * Every landmark testid, count badge, subtitle, viewAll link and
   * child slot survives verbatim.
   */
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100/70 p-5"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
      data-testid={testid}
    >
      <div className="flex items-end justify-between gap-2 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-[14.5px] font-bold tracking-[-0.01em] text-slate-900">{title}</h3>
            {count !== undefined && (
              <span
                className="text-[10px] font-bold tabular-nums px-1.5 h-[18px] rounded-full inline-flex items-center"
                style={{ background: `${accent}1a`, color: accent }}
              >
                {count}
              </span>
            )}
          </div>
          {subtitle && <p className="text-[11.5px] text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {viewAllTo && (
          <Link to={viewAllTo} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
            View all <ChevronRight size={11} />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function ExchangesThisWeek({ items, feeByDeal }) {
  if (!items.length) {
    return (
      <PanelShell title="Exchanges this week" subtitle="Transactions nearing exchange · maintain momentum." count={0} accent="#6366F1" testid="panel-exchanges">
        <p className="text-[12px] text-slate-500 py-3">No exchanges scheduled in the next 7 days.</p>
      </PanelShell>
    );
  }
  return (
    <PanelShell
      title="Exchanges this week"
      subtitle="Transactions nearing exchange · maintain momentum."
      count={items.length}
      accent="#6366F1"
      viewAllTo="/sales-progression"
      testid="panel-exchanges"
    >
      <div className="space-y-2.5">
        {items.slice(0, 4).map((c, i) => {
          const exchanged = String(c.state).toLowerCase() === "exchanged";
          const atRisk = String(c.state).toLowerCase() === "at risk";
          const chip = exchanged ? "Exchanged" : atRisk ? "At risk" : null;
          const tone = chip ? STATUS_CHIP[chip] : null;
          const fee = feeByDeal[c.deal_id];
          const progress = exchanged ? 100 : Math.min(96, Math.max(40, 100 - Math.max(0, c.days_to_exchange) * 8));
          return (
            <Link
              key={c.deal_id}
              to={`/deal-rooms/${c.deal_id}`}
              className="block rounded-xl border border-slate-100/70 px-4 py-3 hover:bg-slate-50/60 transition-colors"
              data-testid={`panel-exchange-row-${i}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[12.5px] font-semibold text-slate-900 truncate">{c.property_address}</p>
                  <p className="text-[10.5px] text-slate-500 mt-0.5 tabular-nums">
                    {c.days_to_exchange === 0 ? "Today"
                      : c.days_to_exchange < 0 ? `Overdue ${-c.days_to_exchange} day${c.days_to_exchange === -1 ? "" : "s"}`
                      : `In ${c.days_to_exchange} day${c.days_to_exchange === 1 ? "" : "s"}`}
                    <span className="text-slate-300 mx-1">·</span>
                    Target exchange
                    {fee ? (
                      <>
                        <span className="text-slate-300 mx-1">·</span>
                        {fmtGBP(fee)} fee
                      </>
                    ) : null}
                  </p>
                </div>
                {chip && (
                  <span
                    className="text-[9.5px] font-bold uppercase tracking-wide px-1.5 h-[18px] rounded inline-flex items-center shrink-0"
                    style={{ background: tone.bg, color: tone.fg }}
                  >
                    {chip}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${progress}%`, background: atRisk ? "#EF4444" : "#10B981" }}
                  />
                </div>
                <span className="text-[10px] tabular-nums text-slate-500">{progress}%</span>
              </div>
            </Link>
          );
        })}
      </div>
    </PanelShell>
  );
}

function WaitingOn({ items }) {
  if (!items.length) {
    return (
      <PanelShell title="Waiting on" subtitle="Parties causing the most delays." count={0} accent="#F59E0B" testid="panel-waiting">
        <p className="text-[12px] text-slate-500 py-3">No party-level delays detected.</p>
      </PanelShell>
    );
  }
  return (
    <PanelShell
      title="Waiting on"
      subtitle="Parties causing the most delays."
      count={items.reduce((a, b) => a + b.deals, 0)}
      accent="#F59E0B"
      viewAllTo="/sales-progression"
      testid="panel-waiting"
    >
      <div className="space-y-2.5">
        {items.slice(0, 4).map((w, i) => {
          const Icon = ownerIcon(w.role);
          const high = w.avg >= 4;
          return (
            <div
              key={w.key}
              className="flex items-center gap-3 rounded-xl px-4 py-3 border border-slate-100/70"
              data-testid={`panel-waiting-row-${i}`}
            >
              <span
                className="w-9 h-9 rounded-xl inline-flex items-center justify-center shrink-0"
                style={{ background: "rgba(245,158,11,0.10)", color: "#B45309" }}
              >
                <Icon size={14} strokeWidth={2.2} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-semibold text-slate-900 truncate">{w.key}</p>
                <p className="text-[10.5px] text-slate-500 mt-0.5">
                  {w.deals} deal{w.deals === 1 ? "" : "s"}
                </p>
              </div>
              <span
                className="text-[10.5px] font-bold tabular-nums px-1.5 h-[20px] rounded inline-flex items-center shrink-0"
                style={{
                  background: high ? "rgba(239,68,68,0.10)" : "rgba(245,158,11,0.12)",
                  color: high ? "#B91C1C" : "#B45309",
                }}
              >
                {/* iter-alignment · P0 · Command Centre Data Reconciliation
                    (2026-02-28) — replace nonsense "0.0d avg" pills
                    (fires when blockers were raised today) with a
                    calmer "Just raised" so the badge reads as real
                    signal rather than broken maths. */}
                {w.avg >= 0.5 ? `${w.avg.toFixed(1)}d avg` : "Just raised"}
              </span>
            </div>
          );
        })}
      </div>
    </PanelShell>
  );
}

function TodaysMovement({ items }) {
  if (!items.length) {
    return (
      <PanelShell title="Today's movement" subtitle="Operational events since this morning." count={0} accent="#10B981" testid="panel-movement">
        <p className="text-[12px] text-slate-500 py-3">No movement yet today.</p>
      </PanelShell>
    );
  }
  return (
    <PanelShell
      title="Today's movement"
      subtitle="Operational events since this morning."
      count={items.length}
      accent="#10B981"
      viewAllTo="/notifications"
      testid="panel-movement"
    >
      <div className="relative">
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-100" aria-hidden />
        <div className="space-y-3.5">
          {items.slice(0, 6).map((r, i) => {
            const ageMin = (() => {
              try {
                return (Date.now() - new Date(r.created_at).getTime()) / 60000;
              } catch { return Infinity; }
            })();
            // tiny freshness pulse:
            //   <30m  → live (emerald + ambient ring)
            //   <24h  → recent (calm slate ring)
            //   ≥24h  → static (no ring)
            const isLive = ageMin < 30;
            const isRecent = !isLive && ageMin < 60 * 24;
            return (
              <Link
                key={r.id}
                to={`/deal-rooms/${r.deal_id}`}
                className="flex items-start gap-3 hover:opacity-90 transition-opacity"
                data-testid={`panel-movement-row-${i}`}
              >
                <span className="relative shrink-0 mt-0.5">
                  <span
                    className={`block w-3.5 h-3.5 rounded-full ring-4 ring-white ${
                      isLive ? "shadow-[0_0_0_4px_rgba(16,185,129,0.18)]" : ""
                    }`}
                    style={{ background: MOVEMENT_DOT[r.verb] || "#94A3B8" }}
                    aria-hidden
                  />
                  {isLive && (
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{ background: "rgba(16,185,129,0.35)", animationDuration: "2200ms" }}
                    />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-bold text-slate-900 leading-tight tracking-[-0.005em] truncate">
                    {r.verb}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1 truncate">{r.deal_name}</p>
                </div>
                <span
                  className={`text-[10px] tabular-nums shrink-0 mt-1 ${
                    isLive
                      ? "text-emerald-600 font-semibold"
                      : isRecent
                      ? "text-slate-500"
                      : "text-slate-400"
                  }`}
                >
                  {relTime(r.created_at)}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </PanelShell>
  );
}

/* ════════════════════════════════════════════════════════════
   Quick Actions — sticky bottom dock
   ════════════════════════════════════════════════════════════ */
const QUICK_ACTIONS = [
  { id: "chase",     Icon: Send,           label: "Chase",            sub: "Send chase",       testid: "qa-chase" },
  { id: "request",   Icon: MessageSquare,  label: "Request update",   sub: "Ask a party",      testid: "qa-request" },
  { id: "seller",    Icon: Inbox,          label: "Send seller update", sub: "Broadcast update", testid: "qa-seller-update" },
  { id: "milestone", Icon: ListChecks,     label: "Update milestone", sub: "Advance a stage",  testid: "qa-milestone" },
  { id: "upload",    Icon: Upload,         label: "Upload document",  sub: "Hub & uploads",    testid: "qa-upload",   to: "/documents" },
  { id: "note",      Icon: Pencil,         label: "Add note",         sub: "Internal record",  testid: "qa-note" },
];

function QuickActionsDock({ onOpen }) {
  const nav = useNavigate();
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 md:left-[var(--cf-sidebar-w,300px)] transition-[left] duration-300 ease-out pointer-events-none"
      data-testid="today-quickactions-dock"
    >
      <div className="pointer-events-auto px-4 md:px-10 pb-3 md:pb-4">
        <div
          className="mx-auto max-w-[1240px] rounded-2xl bg-white/90 backdrop-blur-md border border-slate-200/70 px-3 md:px-4 py-2.5 flex items-center gap-2 md:gap-3 overflow-x-auto"
          style={{ boxShadow: "0 10px 28px -12px rgba(15,23,42,0.16), 0 1px 2px rgba(15,23,42,0.04)" }}
        >
          <div className="hidden md:flex flex-col shrink-0 pr-3 border-r border-slate-200/60">
            <p className="text-[11.5px] font-bold uppercase tracking-[0.12em] text-slate-500 leading-tight">Quick actions</p>
            <p className="text-[10.5px] text-slate-400 leading-tight mt-0.5">Operational commands.</p>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 flex-1 overflow-x-auto cf-scroll">
            {QUICK_ACTIONS.map((qa) => {
              const Icon = qa.Icon;
              return (
                <button
                  key={qa.id}
                  type="button"
                  onClick={() => { if (qa.to) nav(qa.to); else onOpen?.(qa.id); }}
                  className="group h-[42px] pl-2 pr-3 md:pl-2.5 md:pr-3.5 rounded-xl bg-transparent hover:bg-slate-50 inline-flex items-center gap-2.5 shrink-0 transition-colors"
                  data-testid={qa.testid}
                  aria-label={qa.label}
                >
                  <span
                    className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0 transition-colors group-hover:bg-violet-100/70"
                    style={{ background: "rgba(99,102,241,0.07)", color: "#4F46E5" }}
                  >
                    <Icon size={14} strokeWidth={2.4} />
                  </span>
                  <span className="text-[11.5px] font-semibold text-slate-700 group-hover:text-slate-900 leading-tight whitespace-nowrap">
                    {qa.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Helpers
   ════════════════════════════════════════════════════════════ */
function EmptyCard({ icon: Icon, iconBg, iconFg, title, subtitle }) {
  return (
    <div
      className="rounded-2xl bg-white border border-slate-100/70 p-5 flex items-start gap-3"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.03)" }}
    >
      <span
        className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
        style={{ background: iconBg, color: iconFg }}
      >
        <Icon size={15} strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-slate-900">{title}</p>
        <p className="text-[11.5px] text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="mt-12 flex flex-col items-center gap-3 py-16 text-slate-400" data-testid="today-loading">
      <Loader2 size={20} className="animate-spin text-violet-500" />
      <p className="text-[12px]">Gathering today's signals…</p>
    </div>
  );
}
